import {
  Box,
  Divider,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';

import { CreditAgreement } from '@finley-technologies/finley-types';

import { CreditFacilityGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { FormulaContent } from './CalculationTerminologiesTableComponents';
import { getCalcTermDefinitionAndFormulaForLabel } from './calculationTerminologiesUtils';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';

export const CalculationTerminologiesPopover = ({
  termKeyLabel,
  creditAgreementArtifacts,
  creditFacilities,
}: {
  termKeyLabel?: string;
  creditAgreementArtifacts?:
    | CreditAgreement.DomainEntity.CreditAgreementFullArtifact[]
    | null;
  creditFacilities?:
    | Pick<CreditFacilityGQLType, 'id' | 'slug' | 'name'>[]
    | null;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const formulaAndDefinition = getCalcTermDefinitionAndFormulaForLabel({
    termKeyLabel,
    creditAgreementArtifacts,
    creditFacilities,
  });

  if (!formulaAndDefinition?.formula && !formulaAndDefinition?.definition) {
    return <></>;
  }

  const handleInfoIconOnClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    onOpen();
    // the alert icon could be on an active accordion row
    // we do not want the accordion expansion to toggle when specifically clicking the info icon
    event.stopPropagation();
  };

  const handleCloseButtonOnClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    onClose();
    // the alert icon could be on an active accordion row
    // we do not want the accordion expansion to toggle when specifically clicking the info icon
    event.stopPropagation();
  };

  return (
    <Popover isOpen={isOpen} onClose={onClose}>
      <PopoverTrigger>
        <Flex
          cursor="pointer"
          onClick={(event) => handleInfoIconOnClick(event)}
        >
          <Icon icon="circle-info" color={colors.info} />
        </Flex>
      </PopoverTrigger>
      <PopoverContent
        backgroundColor={colors.gray[800]}
        color="white"
        width="400px"
        border="0"
        boxShadow="0px 1px 3px 0px rgba(0, 0, 0, 0.30), 0px 4px 8px 0px rgba(0, 0, 0, 0.15)"
        onClick={(event) => {
          event.stopPropagation();
        }}
        cursor="text"
      >
        <Flex
          onClick={(event) => handleCloseButtonOnClick(event)}
          cursor="pointer"
          width="fit-content"
          position="absolute"
          top="8px"
          right="8px"
        >
          <Icon icon="x" color="white" />
        </Flex>
        <PopoverBody textAlign="left">
          <Box p="8px">
            {formulaAndDefinition?.definition && (
              <Box mb="16px">
                <Text fontWeight={600} fontSize="16px">
                  Definition
                </Text>
                <Divider my="8px" />
                <Text fontWeight={400} fontSize="16px" userSelect="text">
                  {formulaAndDefinition.definition}
                </Text>
              </Box>
            )}
            {formulaAndDefinition?.formula && (
              <Box>
                <Text fontSize="16px" fontWeight={600}>
                  Formula
                </Text>
                <Divider my="8px" />
                <FormulaContent cellValue={formulaAndDefinition.formula} />
              </Box>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
